import { i18n } from '@/i18n';
import { watchEffect } from 'vue';
import { SITE_URL } from '@/seo/siteContent.js';

/**
 * Keeps <head> in step with the page after client-side navigation, for the
 * crawlers that run JavaScript (Google) and for shared links. The static
 * pages written at build time carry the same values for those that don't.
 *
 * `meta` is a getter returning { title, description, path, jsonLd?, lang?, alternates? }
 * — `alternates` are the page's other language versions ([{ hreflang, href }]).
 */
export function usePageMeta(meta) {
  watchEffect(() => {
    const { title, description, path, jsonLd, lang, alternates } = meta() ?? {};
    if (!title) return;

    const url = `${SITE_URL}${path ?? location.pathname}`;
    document.title = title;
    setContent('meta[name="description"]', description);
    setContent('meta[property="og:title"]', title);
    setContent('meta[property="twitter:title"]', title);
    setContent('meta[property="og:description"]', description);
    setContent('meta[property="twitter:description"]', description);
    setContent('meta[property="og:url"]', url);
    setContent('meta[property="twitter:url"]', url);
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', url);

    document.documentElement.lang = lang ?? i18n.global.locale.value;
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    for (const alt of alternates ?? []) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = alt.hreflang;
      link.href = alt.href;
      document.head.appendChild(link);
    }

    if (jsonLd) {
      const script = document.querySelector('script[type="application/ld+json"]');
      if (script) script.textContent = JSON.stringify(jsonLd);
    }
  });
}

function setContent(selector, value) {
  if (value == null) return;
  document.querySelector(selector)?.setAttribute('content', value);
}
